<template>
  <pui-flex class="poster-main-type-selector poster-main-type-selector__root" flex="1">
    <template v-if="singleLoader && isBuildingType">
      <slot name="singleLoader">
        <pui-common-spinner :size="20" :strokeWidth="2" :hideMessage="true" position="fixed" />
      </slot>
    </template>

    <poster-type-gallery
      :scopeFilter="PosterScopes.Creation"
      :typeThumbnailListeners="
        (type, family) => ({
          click: () => addToCart(type, family),
          mouseenter: () => $emit('hoverType', type),
          mouseleave: () => mouseLeaveType(),
        })
      "
      :typeThumbnailProps="
        type => ({
          clickable: !buildingForTypeMap[type.itemId] && !successForTypeMap[type.itemId],
        })
      "
      @changeFamily="creationFamilyId = $event.itemId"
      :initialFamilyId="creationFamilyId"
      class="poster-main-type-selector__type-gallery"
    >
      <template #title>
        <slot name="title"></slot>
      </template>

      <template #typeCenter="{ item: type, family }">
        <pui-common-spinner
          v-if="!singleLoader && buildingForTypeMap[type.itemId]"
          :size="20"
          :strokeWidth="2"
          :hideMessage="true"
        ></pui-common-spinner>
        <pui-thumbnail-main-action v-else @click.stop="addToCart(type, family)">
          {{ $t('poster.creation.type.select') }}
        </pui-thumbnail-main-action>
      </template>

      <template #typeActions="{ item: type , family }">
        <!-- <pui-thumbnail-action
          :loading="buildingForTypeMap[type.itemId]"
          :active="buildingForTypeMap[type.itemId] || successForTypeMap[type.itemId]"
          @click.stop="addToCart(type, family)"
          :picto="successForTypeMap[type.itemId] ? 'mdi-check' : 'mdi-cart-plus'"
        ></pui-thumbnail-action> -->
      </template>
    </poster-type-gallery>
  </pui-flex>
</template>

<script>
import { mapGetters } from 'vuex';

import store from '../../../../core/store';
import { PosterScopes } from '../../constants';
import { getPosterService } from '../../services';
import {
  NAMESPACE as POSTER_NAMESPACE,
  SET_CREATION_FAMILY_ID,
  SET_CURRENT_SIGNAGE_ITEM_ID,
  SET_LAST_BUILT_FAMILY,
  SET_LAST_BUILT_TYPE,
} from '../../store/modules/poster';

export default {
  name: 'poster-main-type-selector',
  props: {
    /**
     * If a single loader should be shown over the whole
     * component when a type is selected. If false,
     * a second type could be selected while the first is being built
     */
    singleLoader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      buildingForTypeMap: {},
      successForTypeMap: {},
      successTimeoutForTypeMap: {},
      // Constants
      PosterScopes,
    };
  },
  computed: {
    ...mapGetters({
      user: 'user',
    }),
    /**
     * Gets or sets the poster cart in the store
     */
    posterCart: {
      get() {
        return getPosterService('cartState').get();
      },
      set(nextSignageItems) {
        getPosterService('cartState').set(nextSignageItems);
      },
    },
    /**
     * Gets or sets the poster creation family id in the store
     */
    creationFamilyId: {
      get() {
        return store.getters[`${POSTER_NAMESPACE}/creationFamily`];
      },
      set(nextFamilyId) {
        store.commit(`${POSTER_NAMESPACE}/${SET_CREATION_FAMILY_ID}`, nextFamilyId);
      },
    },
    /**
     * @returns {boolean} if any type is being built
     */
    isBuildingType() {
      return Object.values(this.buildingForTypeMap).some(val => !!val);
    },
  },
  methods: {
    /**
     * Called when the 'mouseleave' event is fired for a type thumbnail
     */
    mouseLeaveType() {
      // If using 'singleLoader', the spinner will trigger the
      // 'mouseleave' event after the spinner has started.
      // Instead to simulate the mouseleave after the 'buildingType'
      // event, do not emit it at this moment here.
      if (!this.isBuildingType) {
        this.$emit('hoverType', null);
      }
    },
    /**
     * Creates and adds a new poster to the cart for the corresponding type
     *
     * @param {object} type - the poster type to create
     * @param {object} family - the poster family which the type belongs to
     */
    async addToCart(type, family) {
      // Emit the 'hoverType(null)' event to clear the current type
      // _before the 'buildingType' event
      this.$emit('hoverType', null);
      this.$emit('buildingType', type);

      this.$set(this.buildingForTypeMap, type.itemId, true);
      const poster = await getPosterService('signages').buildCartPoster(
        family.itemId,
        type.itemId,
        this.user.extendedProperties
      );
      // Commit new current signage item
      this.posterCart.unshift(poster);
      // Select the new poster
      store.commit(`${POSTER_NAMESPACE}/${SET_CURRENT_SIGNAGE_ITEM_ID}`, poster.id);

      store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_FAMILY}`, family);
      store.commit(`${POSTER_NAMESPACE}/${SET_LAST_BUILT_TYPE}`, type);

      this.$set(this.buildingForTypeMap, type.itemId, false);
      this.flashSuccess(type.itemId);

      this.$emit('addedPoster', poster);
    },
    /**
     * Flashes the success icon for a poster type
     *
     * @param {string} typeId - the type for which to show success
     */
    flashSuccess(typeId) {
      this.$set(this.successForTypeMap, typeId, true);
      if (this.successTimeoutForTypeMap[typeId]) {
        clearTimeout(this.successTimeoutForTypeMap[typeId]);
        this.$delete(this.successTimeoutForTypeMap, typeId);
      }
      const timeoutId = setTimeout(() => {
        this.$set(this.successForTypeMap, typeId, false);
        this.$set(this.successTimeoutForTypeMap, typeId, null);
      }, 1000);
      this.$set(this.successTimeoutForTypeMap, typeId, timeoutId);
    },
  },
  /**
   * Component before destroy hook
   */
  beforeDestroy() {
    // Cleanup timeouts
    if (this.successTimeoutForTypeMap) {
      Object.values(this.successTimeoutForTypeMap).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    }
  },
};
</script>
